import { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { Icon, LoadingIcon, Stack, Text } from '@eluve/components';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import { PdfViewerContent } from './PdfViewerContent';
import { downloadUrl } from './document-creation';

type FillWarnings = {
  skippedFields?: string[];
  alteredFields?: string[];
  usedAppearanceFallback?: boolean;
  excludedHumanOnlyFields?: string[];
};

export type PdfDocumentTabContentProps = {
  /** document_references id; the backend signs its file for any authorized viewer. */
  documentId: string;
  title: string;
  /** Fill warnings persisted in document_references.meta, if any. */
  meta?: FillWarnings | null;
};

/**
 * Renders a persisted PDF document (content_type application/pdf). Signs the
 * backing file through a document-scoped endpoint — so admins and co-clinicians
 * who can see the document (not just its author) can load it.
 */
export const PdfDocumentTabContent: React.FC<PdfDocumentTabContentProps> = ({
  documentId,
  title,
  meta,
}) => {
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams();
  const [url, setUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!tenantId || !documentId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.documents.getFilledDocumentUrl({
          params: { tenantId, documentId },
        });
        if (cancelled) return;
        if (res.status === 200 && res.body.presignedUrl) {
          setUrl(res.body.presignedUrl);
        } else {
          setHasError(true);
        }
      } catch {
        if (!cancelled) setHasError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // apiClient is a stable context value; intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, documentId]);

  if (hasError) {
    return (
      <Stack fullHeight justify="center" align="center" gap={2}>
        <Icon name="AlertCircle" size="2xl" className="text-text-negative" />
        <Text color="secondary">Couldn&apos;t load this PDF.</Text>
      </Stack>
    );
  }

  if (!url) {
    return (
      <Stack fullHeight justify="center" align="center">
        <LoadingIcon size="lg" />
      </Stack>
    );
  }

  const fileName = `${title}.pdf`;
  return (
    <PdfViewerContent
      fileUrl={url}
      fileName={fileName}
      onDownload={() => downloadUrl(url, fileName)}
      skippedFields={meta?.skippedFields}
      alteredFields={meta?.alteredFields}
      usedAppearanceFallback={meta?.usedAppearanceFallback}
      excludedHumanOnlyFields={meta?.excludedHumanOnlyFields}
    />
  );
};
